import {useAccessToken} from "../../services/auth"
import React from "react"
import TitleBar from "../../components/TitleBar/TitleBar"
import {MainSection} from "../../containers/MainSection/MainSection"
import {useRefreshToken} from "../../hooks/useRefreshToken"
import {AsyncResultZipComponent} from "../../containers/AsyncResult/AsyncResult"
import {useAsyncResult} from "../../hooks/useAsyncResult"
import {getByToken, OrgSettings} from "../../resources/orgSettings"
import classNames from "classnames"
import SamlSettings from "./SamlSettings"
import StatementSettings from "./StatementSettings"
import ApplicationFormSettings from "./ApplicationFormSettings"
import DashboardSettings from "./DashboardSettings"
import GeneralSettings from "./GeneralSettings"
import JwtSettings from "./JwtSettings"
import {useQueryState} from "use-location-state"
import IntegrationSettings from "./IntegrationSettings"
import {getAssociatedBanks} from "../../resources/org"
import OutreachSettings from "./OutreachSettings"
import CustomerTagInheritanceSettings from "./CustomerTagInheritanceSettings"
import BillPaySettings from "./BillPaySettings"

type OrgSettingsTabs =
	| "General"
	| "SAML"
	| "Statements"
	| "Application Form"
	| "Dashboard Settings"
	| "JWT Settings"
	| "Integrations"
	| "Outreach"
	| "Customer Tag Inheritance"
	| "Bill Pay"

function TabContent({
	currentTab,
	orgSettings,
	bankNames,
	refresh,
}: {
	currentTab: OrgSettingsTabs
	orgSettings: OrgSettings
	bankNames: string[]
	refresh: () => void
}) {
	if (currentTab == "General") {
		return <GeneralSettings orgSettings={orgSettings} bankNames={bankNames} refresh={refresh} />
	} else if (currentTab == "SAML") {
		return <SamlSettings refresh={refresh} />
	} else if (currentTab == "Statements") {
		return <StatementSettings orgSettings={orgSettings} refresh={refresh} />
	} else if (currentTab == "Application Form") {
		return <ApplicationFormSettings orgSettings={orgSettings} refresh={refresh} />
	} else if (currentTab == "Dashboard Settings") {
		return <DashboardSettings orgSettings={orgSettings} refresh={refresh} />
	} else if (currentTab == "JWT Settings") {
		return <JwtSettings orgSettings={orgSettings} refresh={refresh} />
	} else if (currentTab == "Integrations") {
		return <IntegrationSettings orgSettings={orgSettings} refresh={refresh} />
	} else if (currentTab == "Outreach") {
		return <OutreachSettings refresh={refresh} />
	} else if (currentTab == "Customer Tag Inheritance") {
		return <CustomerTagInheritanceSettings orgSettings={orgSettings} refresh={refresh} />
	} else if (currentTab == "Bill Pay") {
		return <BillPaySettings refresh={refresh} />
	}

	return null
}

function OrgSettingsInternal({
	orgSettings,
	bankNames,
	refresh,
	currentTab,
	setCurrentTab,
}: {
	orgSettings: OrgSettings
	bankNames: string[]
	refresh: () => void
	currentTab: OrgSettingsTabs
	setCurrentTab: (tab: OrgSettingsTabs) => void
}) {
	return (
		<>
			<TitleBar title={"Org Settings"} className={"title-bar-no-border"} />
			<nav className="org-settings tabs-container">
				<div className="tabs">
					<ul>
						<li className={classNames(currentTab == "General" && "is-active")} onClick={() => setCurrentTab("General")}>
							<a>General</a>
						</li>
						<li className={classNames(currentTab == "SAML" && "is-active")} onClick={() => setCurrentTab("SAML")}>
							<a>SAML</a>
						</li>
						<li
							className={classNames(currentTab == "Statements" && "is-active")}
							onClick={() => setCurrentTab("Statements")}
						>
							<a>Branding</a>
						</li>
						<li
							className={classNames(currentTab == "Application Form" && "is-active")}
							onClick={() => setCurrentTab("Application Form")}
						>
							<a>Application Form</a>
						</li>
						<li
							className={classNames(currentTab == "Dashboard Settings" && "is-active")}
							onClick={() => setCurrentTab("Dashboard Settings")}
						>
							<a>Dashboard Settings</a>
						</li>
						<li
							className={classNames(currentTab == "JWT Settings" && "is-active")}
							onClick={() => setCurrentTab("JWT Settings")}
						>
							<a>JWT Settings</a>
						</li>

						<li
							className={classNames(currentTab == "Integrations" && "is-active")}
							onClick={() => setCurrentTab("Integrations")}
						>
							<a>Integrations</a>
						</li>

						<li
							className={classNames(currentTab == "Outreach" && "is-active")}
							onClick={() => setCurrentTab("Outreach")}
						>
							<a>Outreach</a>
						</li>

						<li
							className={classNames(currentTab == "Customer Tag Inheritance" && "is-active")}
							onClick={() => setCurrentTab("Customer Tag Inheritance")}
						>
							<a>Customer Tag Inheritance</a>
						</li>

						<li
							className={classNames(currentTab == "Bill Pay" && "is-active")}
							onClick={() => setCurrentTab("Bill Pay")}
						>
							<a>Bill Pay</a>
						</li>
					</ul>
				</div>
			</nav>

			<MainSection>
				<TabContent currentTab={currentTab} refresh={refresh} orgSettings={orgSettings} bankNames={bankNames} />
			</MainSection>
		</>
	)
}

export default function OrgSettingsPage() {
	const accessToken = useAccessToken()
	const [refreshToken, refresh] = useRefreshToken()
	const [currentTab, setCurrentTab] = useQueryState<OrgSettingsTabs>("tab", "General")
	const orgSettingsAsync = useAsyncResult(() => getByToken(accessToken), [refreshToken])
	const associatedBanks = useAsyncResult(() => getAssociatedBanks(accessToken), [refreshToken])

	return (
		<AsyncResultZipComponent asyncResult1={orgSettingsAsync} asyncResult2={associatedBanks}>
			{({value1: orgSettings, value2: associatedBanks}) => {
				return (
					<OrgSettingsInternal
						orgSettings={orgSettings as OrgSettings}
						bankNames={associatedBanks.attributes?.banks?.map((bank) => bank.name) ?? []}
						refresh={refresh}
						currentTab={currentTab}
						setCurrentTab={(t) => {
							setCurrentTab(t)
						}}
					/>
				)
			}}
		</AsyncResultZipComponent>
	)
}
